import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Calendar, Clock, MapPin, Phone, CheckCircle, XCircle, AlertTriangle,
  Play, Pause, Check, Camera, FileText, Wrench, ExternalLink, Plus,
  Truck, Sparkles, Siren, ClipboardList
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useToast } from '../../components/Toast'
import PhotoUploadButton from '../../components/mobile/PhotoUploadButton'
import WorkOrderSignDialog from '../../components/mobile/WorkOrderSignDialog'

/**
 * Mobile Worker Dashboard
 * Shows assigned InstallationJobs for the logged-in team for today and tomorrow.
 * Optimized for tablets and smartphones.
 */
export default function MobileWorkerDashboard() {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selectedJob, setSelectedJob] = useState(null)
  const [showSignDialog, setShowSignDialog] = useState(false)

  // Fetch team jobs
  const { data: jobs, isLoading } = useQuery({
    queryKey: ['mobile-worker-jobs'],
    queryFn: async () => {
      const response = await api.get('/mobile/worker/jobs', {
        params: { days_ahead: 2 }
      })
      return response.data
    }
  })

  // Start job mutation
  const startJobMutation = useMutation({
    mutationFn: async (jobId) => {
      const response = await api.post(`/mobile/worker/jobs/${jobId}/start`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-worker-jobs'])
      toast.success('Job gestart')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij starten job')
    }
  })

  // Complete job mutation
  const completeJobMutation = useMutation({
    mutationFn: async ({ jobId, actualHours, notes }) => {
      const formData = new FormData()
      if (actualHours) formData.append('actual_hours', actualHours.toString())
      if (notes) formData.append('notes', notes)
      
      const response = await api.post(`/mobile/worker/jobs/${jobId}/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-worker-jobs'])
      toast.success('Job afgerond')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij afronden job')
    }
  })

  // Handle quick actions
  const handleRoute = (googleMapsUrl) => {
    if (googleMapsUrl) {
      window.open(googleMapsUrl, '_blank')
    }
  }

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`
    }
  }

  const handleStart = (jobId) => {
    startJobMutation.mutate(jobId)
  }

  const handleComplete = (jobId) => {
    // Show completion dialog with hours input
    const hours = prompt('Aantal gewerkte uren:')
    if (hours !== null) {
      const notes = prompt('Opmerkingen (optioneel):')
      completeJobMutation.mutate({
        jobId,
        actualHours: parseFloat(hours) || null,
        notes: notes || null
      })
    }
  }

  const handleViewDetails = (jobId) => {
    navigate(`/mobile/jobs/${jobId}`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-canvas dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  // Group jobs by date
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const jobsToday = (jobs || []).filter(job => {
    if (!job.start_date) return false
    const jobDate = new Date(job.start_date)
    jobDate.setHours(0, 0, 0, 0)
    return jobDate.getTime() === today.getTime()
  })

  const jobsTomorrow = (jobs || []).filter(job => {
    if (!job.start_date) return false
    const jobDate = new Date(job.start_date)
    jobDate.setHours(0, 0, 0, 0)
    return jobDate.getTime() === tomorrow.getTime()
  })

  const jobsOther = (jobs || []).filter(job => {
    if (!job.start_date) return true
    const jobDate = new Date(job.start_date)
    jobDate.setHours(0, 0, 0, 0)
    return jobDate.getTime() !== today.getTime() && jobDate.getTime() !== tomorrow.getTime()
  })

  const getStatusColor = (status) => {
    switch (status) {
      case 'PLANNED':
      case 'CONFIRMED':
        return 'badge-active'
      case 'IN_PROGRESS':
        return 'badge-warning'
      case 'COMPLETED':
        return 'badge-success'
      default:
        return 'badge-concept'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PLANNED':
        return 'Gepland'
      case 'CONFIRMED':
        return 'Bevestigd'
      case 'IN_PROGRESS':
        return 'In uitvoering'
      case 'COMPLETED':
        return 'Voltooid'
      default:
        return status
    }
  }

  const getJobTypeIcon = (jobType) => {
    switch (jobType) {
      case 'LEVERING':
        return Truck
      case 'PLAATSING':
        return Wrench
      case 'AFWERKING':
        return Sparkles
      case 'SERVICE':
        return Siren
      default:
        return ClipboardList
    }
  }

  return (
    <div className="min-h-screen bg-canvas dark:bg-gray-900 p-4 space-y-6 pb-24">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink dark:text-white">Mijn Planning</h1>
        <p className="text-sm text-muted mt-1">
          {jobs?.length || 0} job{jobs?.length !== 1 ? 's' : ''} gepland
        </p>
      </div>

      {/* Today's Jobs */}
      {jobsToday.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-ink dark:text-white mb-3">
            Vandaag ({jobsToday.length})
          </h2>
          <div className="space-y-3">
            {jobsToday.map((job) => (
              <JobCard
                key={job.job_id}
                job={job}
                onRoute={() => handleRoute(job.google_maps_url)}
                onCall={() => handleCall(job.contact_phone || job.client_phone)}
                onStart={() => handleStart(job.job_id)}
                onComplete={() => handleComplete(job.job_id)}
                onViewDetails={() => handleViewDetails(job.job_id)}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                getJobTypeIcon={getJobTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tomorrow's Jobs */}
      {jobsTomorrow.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-ink dark:text-white mb-3">
            Morgen ({jobsTomorrow.length})
          </h2>
          <div className="space-y-3">
            {jobsTomorrow.map((job) => (
              <JobCard
                key={job.job_id}
                job={job}
                onRoute={() => handleRoute(job.google_maps_url)}
                onCall={() => handleCall(job.contact_phone || job.client_phone)}
                onStart={() => handleStart(job.job_id)}
                onComplete={() => handleComplete(job.job_id)}
                onViewDetails={() => handleViewDetails(job.job_id)}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                getJobTypeIcon={getJobTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other Jobs */}
      {jobsOther.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-ink dark:text-white mb-3">
            Overige ({jobsOther.length})
          </h2>
          <div className="space-y-3">
            {jobsOther.map((job) => (
              <JobCard
                key={job.job_id}
                job={job}
                onRoute={() => handleRoute(job.google_maps_url)}
                onCall={() => handleCall(job.contact_phone || job.client_phone)}
                onStart={() => handleStart(job.job_id)}
                onComplete={() => handleComplete(job.job_id)}
                onViewDetails={() => handleViewDetails(job.job_id)}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                getJobTypeIcon={getJobTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!jobs || jobs.length === 0) && (
        <div className="bg-surface dark:bg-gray-800 rounded-card shadow-card border border-line dark:border-gray-700 p-8">
          <div className="r11-empty">
            <Calendar className="r11-empty__icon" />
            <p className="r11-empty__title">Geen jobs gepland</p>
          </div>
        </div>
      )}
    </div>
  )
}

// Job Card Component
function JobCard({ 
  job, 
  onRoute, 
  onCall, 
  onStart, 
  onComplete, 
  onViewDetails,
  getStatusColor,
  getStatusLabel,
  getJobTypeIcon
}) {
  const JobTypeIcon = getJobTypeIcon(job.job_type)
  return (
    <div className="bg-surface dark:bg-gray-800 rounded-card shadow-card border border-line dark:border-gray-700 p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <JobTypeIcon className="w-6 h-6 text-accent flex-shrink-0" />
            <h3 className="font-semibold text-ink dark:text-white">{job.project_name}</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className={`badge ${getStatusColor(job.status)}`}>
              {getStatusLabel(job.status)}
            </span>
            {job.estimated_days && (
              <span className="text-xs text-muted">
                {job.estimated_days} dag{job.estimated_days !== 1 ? 'en' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Address & Contact */}
      {job.address && (
        <div className="flex items-start gap-2 mb-2 text-sm text-muted">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{job.address}</span>
        </div>
      )}

      {(job.contact_name || job.client_name) && (
        <div className="text-sm text-muted mb-2">
          <span className="font-medium text-ink dark:text-gray-200">{job.contact_name || job.client_name}</span>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2 mt-4">
        {job.google_maps_url && (
          <button
            onClick={onRoute}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-accent text-white rounded-control hover:bg-accent-700 text-sm font-semibold"
          >
            <ExternalLink className="w-4 h-4" />
            Route
          </button>
        )}
        {(job.contact_phone || job.client_phone) && (
          <button
            onClick={onCall}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-control hover:bg-green-700 text-sm font-semibold"
          >
            <Phone className="w-4 h-4" />
            Bel Klant
          </button>
        )}
        {job.status === 'PLANNED' || job.status === 'CONFIRMED' ? (
          <button
            onClick={onStart}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-control hover:bg-amber-600 text-sm font-semibold"
          >
            <Play className="w-4 h-4" />
            Starten
          </button>
        ) : job.status === 'IN_PROGRESS' ? (
          <button
            onClick={onComplete}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-control hover:bg-green-700 text-sm font-semibold"
          >
            <Check className="w-4 h-4" />
            Afronden
          </button>
        ) : null}
        <button
          onClick={onViewDetails}
          className="px-3 py-2 bg-canvas dark:bg-gray-700 text-ink dark:text-gray-200 border border-line dark:border-gray-600 rounded-control hover:bg-gray-100 dark:hover:bg-gray-600 text-sm font-semibold"
        >
          Details
        </button>
      </div>
    </div>
  )
}
